import useSubmitBook from "api/BookResource/useSubmitBook";
import useFetchCategories from "api/CategoryResource/useFetchCategories";
import useGenres from "api/GenreResource/useGenres";
import {
	getSubmitButtonLabel,
	hasSelectedFiles,
	isActiveRequested,
	isAcceptedBookFile,
} from "api/BookResource/bookFileUpload";
import { BookFilesFields, BookFilesUploadModal } from "components/BookUpload";
import { Button, ButtonLoader, CheckBox, SelectField, TextField } from "components";
import { Form, Formik } from "formik";
import Dialog from "components/Overlays/ResponsiveDialog";
import React from "react";
import { FiPlus } from "react-icons/fi";
import useAuthStore from "store/authStore";
import { MAX_BOOK_COVER_MB, MAX_BOOK_PDF_MB } from "utils";
import { Role } from "utils/server-constants/roles";
import * as Yup from "yup";

const AddBook = () => {
	const [display, setDisplay] = React.useState(false);
	const [filesModal, setFilesModal] = React.useState(null);

	const { data: categories, isLoading: isCategoriesLoading } = useFetchCategories();

	const { data: genres, isLoading: isGenresLoading } = useGenres();

	const hasRoles = useAuthStore((state) => state.hasRoles);

	const initialValues = {
		title: "",
		isbn: "",
		author: "",
		publisher_email: "",
		book_cover: null,
		book_file: null,
		price: "",
		credits: "",
		description: "",
		genres: "",
		category_id: "",
		subscription: false,
		sellable: false,
		is_active: false,
		isFree: false,
	};

	const validationSchema = Yup.object()
		.shape({
			title: Yup.string().required("Book title is required"),
			author: Yup.string().required("Author is required"),
			price: Yup.string().required("Price is required"),
			description: Yup.string().required("Book summary is required"),
			category_id: Yup.string().required("Book category is required"),
			genres: Yup.string().required("Book genre is required"),
			book_cover: Yup.mixed()
				.nullable()
				.test(
					"fileSize",
					`Cover image size should not exceed ${MAX_BOOK_COVER_MB}MB`,
					(value) => !value || value.size / 1024 / 1024 <= MAX_BOOK_COVER_MB,
				),
			book_file: Yup.mixed()
				.nullable()
				.test(
					"fileSize",
					`PDF size should not exceed ${MAX_BOOK_PDF_MB}MB`,
					(value) => !value || value.size / 1024 / 1024 <= MAX_BOOK_PDF_MB,
				)
				.test("mimeType", "Only PDF documents are allowed", isAcceptedBookFile),
		})
		.test("activation-files", "Activation requires both a cover and book file", (values) => {
			if (!values?.is_active) return true;
			return Boolean(values?.book_cover && values?.book_file);
		});

	const { mutateAsync, isLoading } = useSubmitBook();

	const handleSubmitBook = async (data) => {
		const result = await mutateAsync({
			...data,
			subscription: data.subscription ? 1 : 0,
			sellable: data.sellable ? 1 : 0,
			is_active: data.is_active ? 1 : 0,
			isFree: data.isFree ? 1 : 0,
		});

		setDisplay(false);

		if (result?.bookId && result?.wantsActive && !result?.hasFiles) {
			setFilesModal({
				bookId: result.bookId,
				title: data.title,
				wantsActive: true,
			});
		}
	};

	return (
		<>
			<button
				type="button"
				onClick={() => setDisplay(true)}
				className="px-2 py-1 text-sm rounded bg-gradient-to-b from-akgreenest to-akgreen text-gray-100 border border-akgreen flex gap-2 items-center"
			>
				Add Book <FiPlus />
			</button>

			<Dialog
				header={
					<div>
						<h2 className="text-lg font-semibold">Add new book</h2>
						<p className="text-sm text-gray-500 mt-1 font-normal">
							Add book details and optionally upload files in one step.
						</p>
					</div>
				}
				visible={display}
				onHide={() => setDisplay(false)}
				style={{ width: "min(560px, 95vw)" }}
				position="center"
				draggable={false}
				modal
			>
				<Formik
					initialValues={initialValues}
					validationSchema={validationSchema}
					onSubmit={(data) => handleSubmitBook(data)}
				>
					{(formik) => {
						const wantsActive = isActiveRequested(formik.values.is_active);
						const hasFiles = hasSelectedFiles(formik.values);

						return (
							<Form>
								<TextField
									type="text"
									name="title"
									placeholder="e.g Rich Dad, Poor Dad"
									label="Title"
								/>

								<TextField
									type="text"
									name="isbn"
									placeholder="e.g 978-7929-543"
									label="ISBN Number"
								/>

								<TextField
									type="text"
									name="author"
									placeholder="e.g Robert Kiyosaki"
									label="Author"
								/>

								{!hasRoles([Role.SeniorPublisher, Role.Publisher]) && (
									<TextField
										type="text"
										name="publisher_email"
										placeholder="e.g publisher@example.com"
										label="Publisher's Email"
									/>
								)}

								<BookFilesFields
									values={formik.values}
									errors={formik.errors}
									touched={formik.touched}
									setFieldValue={formik.setFieldValue}
									setFieldTouched={formik.setFieldTouched}
								/>

								<TextField
									type="number"
									min="0"
									step="0.01"
									name="price"
									placeholder="e.g 100.99"
									label="Book Price"
								/>

								<TextField
									type="number"
									min="0"
									step="0.01"
									name="credits"
									placeholder="e.g 2"
									label="Equivalent stars to the book price"
								/>

								<SelectField
									name="category_id"
									label="Select Book Category"
									placeholder="e.g Academic"
								>
									{!isCategoriesLoading &&
										categories?.data?.map((category) => (
											<option value={category.id} key={category.id}>
												{category.name}
											</option>
										))}
								</SelectField>

								<SelectField name="genres" label="Select Book Collection" placeholder="e.g Fiction">
									{!isGenresLoading &&
										genres?.data?.data?.map((genre) => (
											<option value={genre.id} key={genre.id}>
												{genre.name}
											</option>
										))}
								</SelectField>

								<TextField
									type="textarea"
									name="description"
									placeholder="Start typing..."
									label="Description"
								/>

								<CheckBox
									label="Do you want people to subscribe to this book?"
									name="subscription"
								/>

								<CheckBox label="Can people buy this book?" name="sellable" />

								<CheckBox label="Activate the book?" name="is_active" />

								{wantsActive && (
									<p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2 mb-3">
										{hasFiles
											? "The book will activate automatically after files finish processing."
											: "Activation requires both a cover and book file in this form."}
									</p>
								)}

								<CheckBox label="Make the book free?" name="isFree" />

								<div className="flex mt-3">
									<Button type="reset">Clear</Button>
									{isLoading ? (
										<ButtonLoader size={40} />
									) : (
										<Button type="submit" className="ml-3 flex items-center ">
											{getSubmitButtonLabel({ isLoading, mode: "create" })}
										</Button>
									)}
								</div>
							</Form>
						);
					}}
				</Formik>
			</Dialog>

			{filesModal && (
				<BookFilesUploadModal
					visible={Boolean(filesModal)}
					onHide={() => setFilesModal(null)}
					bookId={filesModal.bookId}
					bookTitle={filesModal.title}
					activateAfterUpload={filesModal.wantsActive}
					allowSkip="create"
				/>
			)}
		</>
	);
};

export default AddBook;
